import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useState, KeyboardEvent, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useCompass } from "../../contexts/CompassContext";

interface CompassInputProps {
  onSend: (message: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function CompassInput({
  onSend,
  placeholder = "Ask Compass anything...",
  disabled = false,
  className = "",
}: CompassInputProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const location = useLocation();
  const { state, setCurrentProduct } = useCompass();

  // Only show product context when on PDP
  const isOnPDP = location.pathname === "/pdp";
  const currentProduct = isOnPDP ? state.currentProduct : undefined;

  // Auto-resize textarea to grow with content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      // Set height to scrollHeight - let it grow naturally
      // Cap at reasonable max (around 5-6 lines)
      const maxHeight = 120; // pixels
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      // Only show scrollbar if content exceeds maxHeight
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    }
  }, [value]);

  const handleRemoveContext = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Just clear the product context
    setCurrentProduct(undefined);
  };

  const handleSend = () => {
    if (value.trim() && !disabled) {
      onSend(value.trim());
      setValue("");
      // Reset textarea height after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={cn("bg-fs-surface-primary relative py-3", className || "px-4")}
    >
      {/* Single bordered container with everything inside */}
      <div
        className="relative overflow-hidden rounded-lg border border-[#dfe0e1] transition-colors focus-within:border-[#333333]"
        style={{ minHeight: "56px" }}
      >
        <div className="flex min-h-[56px] flex-col p-3 pr-14">
          {/* Context pill - show only product context when on PDP */}
          {currentProduct && (
            <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-sm border border-[#dfe0e1] bg-[#f5f5f5] px-2 py-1.5">
              {/* Product image thumbnail */}
              <img
                src={currentProduct.imageUrl}
                alt={currentProduct.name}
                className="h-6 w-6 shrink-0 rounded-sm object-cover"
              />
              <span className="text-fs-text-primary text-sm whitespace-nowrap">
                {currentProduct.name}
              </span>
              <button
                onClick={handleRemoveContext}
                className="flex shrink-0 items-center justify-center rounded-full p-0.5 transition-colors hover:bg-[#e5e5e5]"
                aria-label="Remove context"
              >
                <svg
                  className="h-3 w-3 text-[#757575]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              currentProduct ? "Ask about this product..." : placeholder
            }
            disabled={disabled}
            rows={1}
            className="text-fs-text-primary w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-sm placeholder:text-[#757575] focus:outline-hidden"
            style={{
              minHeight: "20px",
              maxHeight: "120px",
              lineHeight: "20px",
              height: "20px",
            }}
          />
        </div>

        {/* Send button positioned inside the container on the right */}
        <button
          onClick={handleSend}
          disabled={!value.trim() || disabled}
          className="text-fs-text-primary-inverse absolute right-2 bottom-2 flex h-7 w-7 items-center justify-center rounded-lg bg-[#333333] transition-colors hover:bg-[#555555] disabled:cursor-not-allowed disabled:bg-[#dfe0e1]"
          aria-label="Send message"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      </div>
      <style>{`
        textarea::-webkit-scrollbar {
          width: 4px;
        }
        textarea::-webkit-scrollbar-track {
          background: transparent;
        }
        textarea::-webkit-scrollbar-thumb {
          background: #dfe0e1;
          border-radius: 2px;
        }
        textarea::-webkit-scrollbar-thumb:hover {
          background: #757575;
        }
      `}</style>
    </div>
  );
}
